import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useEffect, useRef } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { Box, IconButton, styled, useTheme, type SxProps, type Theme } from "~/common/mui";
import { ControlField } from "./ControlField";
import { TileOption } from "./TileOption";
import type { ControlOptionViewModel, HorizontalCarouselControlViewModel } from "./controlViewModel";

export interface HorizontalCarouselControlProps
  extends Omit<HorizontalCarouselControlViewModel, "type" | "controlIndex"> {
  onChange: (value: string) => void;
  className?: string;
  sx?: SxProps<Theme>;
}

const CarouselRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: `${theme.spacing(5)} minmax(0, 1fr) ${theme.spacing(5)}`,
  alignItems: "center",
  gap: theme.spacing(1),
  minWidth: 0,
  "& .swiper": {
    width: "100%",
    minWidth: 0,
    overflow: "hidden",
  },
  "& .swiper-wrapper": {
    alignItems: "stretch",
  },
  "& .swiper-slide": {
    width: `min(${theme.spacing(33)}, 100%)`,
    height: "auto",
    display: "flex",
  },
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  width: theme.spacing(5),
  height: theme.spacing(5),
  color: theme.palette.primary.contrastText,
  backgroundColor: theme.palette.primary.main,
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
  "&.Mui-disabled": {
    color: theme.palette.action.disabled,
    backgroundColor: theme.palette.action.disabledBackground,
  },
}));

const findSelectableIndex = (options: readonly ControlOptionViewModel[], selectedIndex: number, direction: -1 | 1) => {
  for (let index = selectedIndex + direction; index >= 0 && index < options.length; index += direction) {
    if (!options[index].disabled) {
      return index;
    }
  }
  return -1;
};

const revealSlide = (swiper: SwiperType, index: number) => {
  const slide = swiper.slides[index];
  if (!slide) {
    return;
  }

  const viewportBounds = swiper.el.getBoundingClientRect();
  const slideBounds = slide.getBoundingClientRect();
  if (slideBounds.left < viewportBounds.left || slideBounds.right > viewportBounds.right) {
    swiper.slideTo(index, 0);
  }
};

export const HorizontalCarouselControl = ({
  label,
  info,
  helperText,
  error,
  errorText,
  disabled = false,
  value,
  options,
  layout = "compact",
  previousLabel,
  nextLabel,
  standardLabel,
  onChange,
  className,
  sx,
}: HorizontalCarouselControlProps) => {
  const theme = useTheme();
  const swiperRef = useRef<SwiperType | null>(null);
  const selectedIndex = options.findIndex((option) => option.value === value);
  const previousIndex = selectedIndex >= 0 ? findSelectableIndex(options, selectedIndex, -1) : -1;
  const nextIndex = findSelectableIndex(options, selectedIndex, 1);

  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper || selectedIndex < 0) {
      return;
    }

    swiper.update();
    revealSlide(swiper, selectedIndex);
  }, [options, selectedIndex]);

  const selectByIndex = (index: number) => {
    const option = options[index];
    const swiper = swiperRef.current;
    if (!option) {
      return;
    }

    onChange(option.value);
    if (swiper) {
      revealSlide(swiper, index);
    }
  };

  return (
    <ControlField
      label={label}
      info={info}
      helperText={helperText}
      error={Boolean(error)}
      errorText={errorText}
      className={className}
      sx={sx}
    >
      <CarouselRow>
        <NavigationButton
          aria-label={previousLabel}
          disabled={disabled || previousIndex < 0}
          onClick={() => selectByIndex(previousIndex)}
        >
          <ChevronLeftIcon />
        </NavigationButton>
        <Swiper
          slidesPerView="auto"
          spaceBetween={Number.parseFloat(theme.spacing(2))}
          watchSlidesProgress
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            if (selectedIndex >= 0) {
              revealSlide(swiper, selectedIndex);
            }
          }}
          role="radiogroup"
          aria-label={label}
        >
          {options.map((option) => (
            <SwiperSlide key={option.value}>
              <TileOption
                option={option}
                selected={option.value === value}
                hasError={Boolean(error) && value === null}
                disabled={disabled}
                layout={layout}
                standardLabel={standardLabel}
                onSelect={onChange}
              />
            </SwiperSlide>
          ))}
        </Swiper>
        <NavigationButton
          aria-label={nextLabel}
          disabled={disabled || nextIndex < 0}
          onClick={() => selectByIndex(nextIndex)}
        >
          <ChevronRightIcon />
        </NavigationButton>
      </CarouselRow>
    </ControlField>
  );
};
